import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import uploadBtn from "../assets/uploadBtn.svg";
import RejectionCard from "./RejectionCard";
import ScheduleInterview from "./ScheduleInterview";
import axiosInstance from "../axios/axios";
import Loader from "./Loader";

const ViewApplication = () => {
  const { applicationId } = useParams(); // Get application ID from URL
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appliedStudent, setAppliedStudent] = useState(null);
  const [openRejection, setOpenRejection] = useState(false);
  const [scheduleInterview, setScheduleInterview] = useState(false);
  const [jobQuestions, setJobQuestions] = useState({});
  const org_id = localStorage.getItem("role_specific_id");

  useEffect(() => {
    const fetchViewedApplication = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.put(
          `ReviewApplicationByOrg/${applicationId}`,
          { org_id }
        );
        const response = await axiosInstance.get("all_users");
        const foundUser = response.data.users.find(
          (u) => u.user_id === parseInt(res.data.application.user_id)
        );
        setAppliedStudent(foundUser);
        setApplication(res.data.application || []);

        // Fetch job to get q1-q5
        if (res.data.application && res.data.application.job_id) {
          const jobRes = await axiosInstance.get(`/view/jobs/${res.data.application.job_id}`);
          setJobQuestions({
            q1: jobRes.data.job.q1,
            q2: jobRes.data.job.q2,
            q3: jobRes.data.job.q3,
            q4: jobRes.data.job.q4,
            q5: jobRes.data.job.q5,
          });
        }
      } catch (error) {
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchViewedApplication();
  }, [applicationId, org_id]);

  const handleRejection = () => setOpenRejection(!openRejection);
  const handleScheduleInterview = () => setScheduleInterview(!scheduleInterview);

  if (loading) return <Loader />;
  if (!application) {
    return (
      <div>
        <p>No application details...</p>
        <Link to="/dashboard/applications" className="btn">
          Go Back
        </Link>
      </div>
    );
  }

  // Prepare additional answers
  const additionalAnswers = [
    { question: jobQuestions.q1, answer: application.a1 },
    { question: jobQuestions.q2, answer: application.a2 },
    { question: jobQuestions.q3, answer: application.a3 },
    { question: jobQuestions.q4, answer: application.a4 },
    { question: jobQuestions.q5, answer: application.a5 },
  ];
  const hasAnswers = additionalAnswers.some(
    (item) => item.answer && item.answer !== null && item.answer !== ""
  );

  return (
    <div className="flex flex-col h-screen relative w-full">
      <div className="flex justify-between px-6 py-3 items-center w-full top-bar">
        <span>View Applications</span>
        <Link
          to="/dashboard/create-job"
          className="hidden md:flex gap-2 items-center justify-center rounded-[8px] border-2 border-[#049645] py-[10px] px-[14px]"
        >
          <img src={uploadBtn} alt="Upload Button" className="w-4 h-4" />
          <span className="text-sm">Create Job</span>
        </Link>
      </div>

      <section className="md:mt-[40px] flex px-[24px] flex-col gap-6">
        <div className="flex w-full justify-between custom-section xsm:flex-col">
          <div className="flex flex-col gap-4">
            <span className="label-name">Applicant&apos;s Name</span>
            <span className="content-container">
              {appliedStudent.student.full_name || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <span className="label-name">Applicant&apos;s Email Address</span>
            <span className="content-container">
              {appliedStudent.email || "N/A"}
            </span>
          </div>
        </div>

        <div className="flex w-full justify-between custom-section xsm:flex-col">
          <div className="flex flex-col gap-4">
            <span className="label-name">Applicant&apos;s University</span>
            <span className="content-container">
              {appliedStudent.student.university || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <span className="label-name">Applicant&apos;s Course of Study</span>
            <span className="content-container">
              {appliedStudent.student.course_of_study || "N/A"}
            </span>
          </div>
        </div>

        <div className="flex w-full justify-between custom-section xsm:flex-col">
          <div className="flex flex-col gap-4 w-1/2">
            <span className="label-name">Applicant&apos;s Resume</span>
            <a
              href={`https://www.dashlyx.com/IFJ/storage/app/public/${application.resume}`}
              className="content-container"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{application.resume ? "Download Resume" : "N/A"}</span>
            </a>
          </div>
          <div className="flex flex-col gap-4 ">
            <span className="label-name ">
              Applicant&apos;s Application Letter
            </span>
            <a
              className="content-container"
              href={`https://www.dashlyx.com/IFJ/storage/app/public/${application.internship_letter}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>
                {application.internship_letter ? "Download Letter" : "N/A"}
              </span>
            </a>
          </div>
        </div>
      </section>

      <section className="flex flex-col mt-4">
        <h3 className="p-[24px] pt-0">Cover Letter</h3>
        <span className="mx-[24px] px-[14px] py-[10px] mb-3 border border-[#D0D5DD] rounded-lg">
          {application.cover_letter || "No cover letter provided."}
        </span>
      </section>

      {/* Additional Questions & Answers Preview */}
      {hasAnswers && (
        <section className="flex flex-col mt-4 mx-[24px]">
          <h3 className="font-semibold text-lg mb-2">Additional Questions & Answers</h3>
          <div className="space-y-4">
            {additionalAnswers.map(
              (item, idx) =>
                item.answer &&
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="font-medium text-gray-700 mb-1">
                    {item.question ? item.question : `Question ${idx + 1}`}
                  </div>
                  <div className="text-gray-900">{item.answer}</div>
                </div>
            )}
          </div>
        </section>
      )}

      {openRejection && (
        <RejectionCard
          handleRejection={handleRejection}
          id={org_id}
          applicationId={application.id}
        />
      )}
      {scheduleInterview && (
        <ScheduleInterview
          handleScheduleInterview={handleScheduleInterview}
          applicationId={application.id}
        />
      )}

      <section className="flex justify-between px-[24px] gap-3 xsm:py-4">
        <button className="btn btn-danger w-full" onClick={handleRejection}>
          Reject Application
        </button>
        <button
          className="btn btn-success w-full"
          onClick={handleScheduleInterview}
        >
          Schedule Interview
        </button>
      </section>
    </div>
  );
};

export default ViewApplication;